import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';

import { Icon } from '../../shared/components/Icon';
import { logout } from '../../lib/auth/authApi';
import { useSessionStore } from '../../lib/auth/sessionStore';

const SECTIONS = [
  { to: '/admin', label: 'Overview', end: true },
  { to: '/admin/projects', label: 'Projects', end: false },
  { to: '/admin/experience', label: 'Experience', end: false },
  { to: '/admin/skills', label: 'Skills', end: false },
  { to: '/admin/media', label: 'Media', end: false },
  { to: '/admin/settings', label: 'Settings', end: false },
] as const;

export function AdminLayout() {
  const navigate = useNavigate();
  const session = useSessionStore((state) => state.session);
  const setSession = useSessionStore((state) => state.setSession);

  const signOut = async () => {
    if (session !== null) {
      try {
        await logout(session.accessToken);
      } catch {
        // Signing out locally matters more than the server acknowledging it. The refresh
        // token expires on its own, and leaving the user stuck on the admin page because
        // the network blipped would be the worse failure.
      }
    }

    setSession(null);
    void navigate('/admin/login', { replace: true });
  };

  return (
    <div className="flex min-h-dvh flex-col text-text">
      <header className="sticky top-0 z-20 border-b border-line bg-surface/85 backdrop-blur-[12px]">
        <div className="mx-auto flex w-full max-w-[1080px] flex-wrap items-center justify-between gap-4 px-6 py-3.5">
          <div className="flex items-center gap-3">
            <strong className="text-[17px] font-bold tracking-[-0.02em]">AJ</strong>
            <span className="rounded-sm border border-accent-line px-[7px] py-[3px] text-[9.5px] font-semibold uppercase tracking-label text-accent">
              Admin
            </span>
          </div>

          <div className="flex items-center gap-2">
            <Link
              to="/"
              className="inline-flex items-center gap-2 rounded border border-line px-3.5 py-2 text-xs font-medium text-muted transition-colors hover:border-line-strong hover:bg-panel hover:text-text"
            >
              <Icon name="external" size={14} />
              View site
            </Link>

            <button
              type="button"
              onClick={() => void signOut()}
              className="inline-flex items-center gap-2 rounded border border-line px-3.5 py-2 text-xs font-medium text-muted transition-colors hover:border-danger/40 hover:text-danger"
            >
              Sign out
            </button>
          </div>
        </div>

        <nav aria-label="Admin sections" className="mx-auto w-full max-w-[1080px] px-6">
          <ul className="m-0 flex list-none gap-1 overflow-x-auto p-0">
            {SECTIONS.map((section) => (
              <li key={section.to}>
                <NavLink
                  to={section.to}
                  end={section.end}
                  className={({ isActive }) =>
                    `-mb-px inline-block border-b-2 px-3 py-2.5 text-[11.5px] font-semibold uppercase tracking-label whitespace-nowrap transition-colors ${
                      isActive
                        ? 'border-accent text-text'
                        : 'border-transparent text-faint hover:text-text'
                    }`
                  }
                >
                  {section.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      </header>

      <div className="mx-auto w-full max-w-[1080px] flex-1 px-6 pt-9 pb-[90px]">
        <Outlet />
      </div>
    </div>
  );
}
